import { useAuth } from "@clerk/clerk-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * The active diligence scope: a specific Company, or the whole portfolio (null).
 * Everything company-scoped (Evidence, Diligence, Overview) reads from here in
 * later phases; for now the switcher sets it and persists it across sessions.
 */
const STORAGE_PREFIX = "p365:active-company";

interface ScopeValue {
  /** null = Portfolio / all companies. */
  activeCompanyId: string | null;
  setActiveCompany: (id: string | null) => void;
}

const ScopeContext = createContext<ScopeValue | undefined>(undefined);

function readStored(storageKey: string | null): string | null {
  if (!storageKey) return null;
  try {
    return window.localStorage.getItem(storageKey) || null;
  } catch {
    return null;
  }
}

export function ScopeProvider({ children }: { children: ReactNode }) {
  const { orgId, userId } = useAuth();
  const storageKey = useMemo(
    () => (orgId && userId ? `${STORAGE_PREFIX}:${userId}:${orgId}` : null),
    [orgId, userId],
  );
  const [scope, setScope] = useState<{ storageKey: string | null; companyId: string | null }>({
    storageKey: null,
    companyId: null,
  });

  useEffect(() => {
    setScope({ storageKey, companyId: readStored(storageKey) });
  }, [storageKey]);

  // Never expose the previous tenant's company during the render before the
  // storage-key effect runs after a Clerk organization switch.
  const activeCompanyId = scope.storageKey === storageKey ? scope.companyId : null;

  const setActiveCompany = useCallback((id: string | null) => {
    setScope({ storageKey, companyId: id });
    try {
      if (!storageKey) return;
      if (id) window.localStorage.setItem(storageKey, id);
      else window.localStorage.removeItem(storageKey);
    } catch {
      // Storage can be unavailable (private mode) — scope still works for the session.
    }
  }, [storageKey]);

  return (
    <ScopeContext.Provider value={{ activeCompanyId, setActiveCompany }}>
      {children}
    </ScopeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useScope(): ScopeValue {
  const ctx = useContext(ScopeContext);
  if (!ctx) throw new Error("useScope must be used within a ScopeProvider");
  return ctx;
}
